import type { FC, ReactNode } from 'react'
import type { SiteNav } from '@/lib/content'

import Link from 'next/link'
import { Facebook, Instagram, Mail, MapPin, Phone } from 'lucide-react'

import { Monogram } from '@/components/brand/monogram'
import { Logo } from '@/components/brand/logo'
import { Container, DimensionRule } from '@/components/generals/primitives'
import { contact, siteConfig, social } from '@/constants/site'

interface FooterProps {
    nav?: SiteNav
}

/* lucide-react carries Instagram and Facebook but no WhatsApp mark, so the
   glyph is drawn here. Sized slightly smaller than the stroked icons beside
   it so the three read at the same optical weight. */
const WhatsAppIcon: FC<{ className?: string }> = ({ className }): ReactNode => (
    <svg viewBox='0 0 24 24' fill='currentColor' aria-hidden='true' className={className}>
        <path d='M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z' />
    </svg>
)

const FOOTER_AREA_LIMIT = 8

const companyLinks = [
    { href: '/about', label: 'About the company' },
    { href: '/projects', label: 'Recent projects' },
    { href: '/areas', label: 'Areas we cover' },
    { href: '/contact', label: 'Request a quotation' },
    { href: '/privacy', label: 'Privacy notice' }
]

export const Footer: FC<FooterProps> = ({ nav }): ReactNode => {
    const services = nav?.services ?? []
    const areas = nav?.areas ?? []
    const year = 2026

    return (
        <footer className='relative overflow-hidden bg-ink text-paper'>
            <Monogram
                className='pointer-events-none absolute -bottom-16 -right-10 hidden w-[26rem] text-paper/[0.04] lg:block'
            />

            <Container>
                <div className='relative py-16 lg:py-20'>
                    <div className='grid gap-12 lg:grid-cols-12'>
                        <div className='lg:col-span-4'>
                            <Logo variant='mark' />

                            <p className='mt-5'>
                                <span className='block font-heading text-lg font-bold text-paper'>
                                    Your Home. Expertly Crafted.
                                </span>

                                <span aria-hidden='true' className='mt-2 block h-0.5 w-10 bg-minium' />
                            </p>

                            <p className='mt-6 max-w-sm text-sm leading-relaxed text-ink-muted'>
                                Extensions, loft conversions, structural alterations and full
                                refurbishment for period and interwar homes across South-East London
                                and Kent.
                            </p>

                            <ul className='mt-8 space-y-3 text-sm'>
                                <li>
                                    <a
                                        href={contact.phoneHref}
                                        className='flex items-center gap-3 font-semibold transition-colors hover:text-minium-tint'
                                    >
                                        <Phone aria-hidden='true' className='size-4 shrink-0 text-minium' />
                                        {contact.phone}
                                    </a>
                                </li>

                                <li>
                                    <a
                                        href={contact.emailHref}
                                        className='flex items-center gap-3 break-all text-ink-muted transition-colors hover:text-paper'
                                    >
                                        <Mail aria-hidden='true' className='size-4 shrink-0 text-minium' />
                                        {contact.email}
                                    </a>
                                </li>

                                <li className='flex items-center gap-3 text-ink-muted'>
                                    <MapPin aria-hidden='true' className='size-4 shrink-0 text-minium' />
                                    Southeast London & Kent
                                </li>
                            </ul>

                            <ul className='mt-8 flex items-center gap-3'>
                                <li>
                                    <a
                                        href={social.whatsapp}
                                        target='_blank'
                                        rel='noreferrer noopener'
                                        aria-label={`Message Capital Craft Construction on WhatsApp, ${contact.phone}`}
                                        className='inline-flex size-11 items-center justify-center border border-ink-line text-paper/85 transition-colors duration-200 ease-out hover:border-minium hover:bg-minium hover:text-white'
                                    >
                                        <WhatsAppIcon className='size-[1.05rem]' />
                                    </a>
                                </li>

                                {social.facebook ? (
                                    <li>
                                        <a
                                            href={social.facebook}
                                            target='_blank'
                                            rel='noreferrer noopener'
                                            aria-label='Capital Craft Construction on Facebook'
                                            className='inline-flex size-11 items-center justify-center border border-ink-line text-paper/85 transition-colors duration-200 ease-out hover:border-minium hover:bg-minium hover:text-white'
                                        >
                                            <Facebook aria-hidden='true' className='size-5' />
                                        </a>
                                    </li>
                                ) : null}

                                <li>
                                    <a
                                        href={social.instagram}
                                        target='_blank'
                                        rel='noreferrer noopener'
                                        aria-label={`Capital Craft Construction on Instagram, ${social.instagramHandle}`}
                                        className='inline-flex size-11 items-center justify-center border border-ink-line text-paper/85 transition-colors duration-200 ease-out hover:border-minium hover:bg-minium hover:text-white'
                                    >
                                        <Instagram aria-hidden='true' className='size-5' />
                                    </a>
                                </li>
                            </ul>
                        </div>

                        <div className='lg:col-span-8'>
                            <div className='grid gap-10 sm:grid-cols-3'>
                                <div>
                                    <h2 className='font-mono text-[0.65rem] uppercase tracking-label text-ink-muted'>
                                        Services
                                    </h2>

                                    <ul className='mt-5 space-y-3 text-sm'>
                                        {services.map((service) => (
                                            <li key={service.slug}>
                                                <Link
                                                    href={`/services/${service.slug}`}
                                                    className='text-paper/85 transition-colors hover:text-minium-tint'
                                                >
                                                    <span data-mdlfy-cms={`services:${service.id}:nav_label`}>
                                                        {service.nav_label ?? service.title}
                                                    </span>
                                                </Link>
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                <div>
                                    <h2 className='font-mono text-[0.65rem] uppercase tracking-label text-ink-muted'>
                                        Areas covered
                                    </h2>

                                    <ul className='mt-5 space-y-3 text-sm'>
                                        {areas.slice(0, FOOTER_AREA_LIMIT).map((area) => (
                                            <li key={area.slug}>
                                                <Link
                                                    href={`/areas/${area.slug}`}
                                                    className='text-paper/85 transition-colors hover:text-minium-tint'
                                                >
                                                    <span data-mdlfy-cms={`areas:${area.id}:name`}>
                                                        {area.name}
                                                    </span>
                                                </Link>
                                            </li>
                                        ))}

                                        {areas.length > FOOTER_AREA_LIMIT ? (
                                            <li>
                                                <Link
                                                    href='/areas'
                                                    className='font-mono text-[0.65rem] uppercase tracking-label text-minium-tint transition-colors hover:text-paper'
                                                >
                                                    All {areas.length} areas
                                                </Link>
                                            </li>
                                        ) : null}
                                    </ul>
                                </div>

                                <div>
                                    <h2 className='font-mono text-[0.65rem] uppercase tracking-label text-ink-muted'>
                                        Company
                                    </h2>

                                    <ul className='mt-5 space-y-3 text-sm'>
                                        {companyLinks.map((item) => (
                                            <li key={item.href}>
                                                <Link
                                                    href={item.href}
                                                    className='text-paper/85 transition-colors hover:text-minium-tint'
                                                >
                                                    {item.label}
                                                </Link>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>

                    <DimensionRule tone='paper' className='mt-14' />

                    <div className='mt-8 flex flex-col gap-4 text-xs text-ink-muted sm:flex-row sm:items-center sm:justify-between'>
                        <p>
                            {siteConfig.legalName}. Registered in England and Wales.
                        </p>

                        <p className='font-mono uppercase tracking-label'>
                            Built in South-East London, {year}
                        </p>
                    </div>
                </div>
            </Container>
        </footer>
    )
}
